import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Button from 'components/atoms/Button';
import { getSortedLanguages, searchLanguages, type Language } from 'utils/languages';
import { useScrollLock } from 'utils/hooks';
import { useTranslation } from 'react-i18next';

const FullScreenOverlay = styled.div`
  ${({ theme: { colors, depths } }) => css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: ${depths.priority * 10};
    background-color: ${colors.neutral.white};
    display: flex;
    align-items: center;
    justify-content: center;
  `}
`;

const ModalContainer = styled.div`
  ${({ theme: { colors, mq } }) => css`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    background-color: ${colors.neutral.white};
  `}
`;

const Header = styled.div`
  ${({ theme: { colors, utils, mq } }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: ${utils.rem(50)} ${utils.rem(16)} ${utils.rem(12)};
    background-color: ${colors.neutral.white};

    ${mq.md.up} {
      padding: ${utils.rem(24)} ${utils.rem(24)} ${utils.rem(16)};
    }
  `}
`;

const Title = styled.h1`
  ${({ theme: { colors, tp } }) => css`
    ${tp.h1};
    color: ${colors.neutral.black};
    margin: 0 0 1rem;
    white-space: pre-line;
  `}
`;

const Description = styled.p`
  ${({ theme: { colors, tp } }) => css`
    ${tp.p1};
    font-weight: 400;
    color: ${colors.primary.main};
    opacity: 0.7;
    margin: 0 0 1.5rem;
    max-width: 320px;
  `}
`;

const SearchContainer = styled.div`
  ${({ theme: { colors, utils } }) => css`
    position: relative;
    padding: 0 ${utils.rem(16)} ${utils.rem(8)};
    background-color: ${colors.neutral.white};
  `}
`;

const SearchInput = styled.input`
  ${({ theme: { colors, utils } }) => css`
    font-size: ${utils.rem(16)};
    line-height: 1.5;
    width: 100%;
    padding: ${utils.rem(10)} ${utils.rem(12)};
    border: 1px solid ${colors.neutral.grey};
    border-radius: ${utils.rem(8)};
    background-color: ${colors.neutral.grey};
    color: ${colors.neutral.black};
    outline: none;
    transition: all 0.2s;

    &::placeholder {
      color: ${colors.primary.main};
      opacity: 0.4;
    }

    &:focus {
      border-color: ${colors.primary.light};
      background-color: ${colors.neutral.white};
    }
  `}
`;

const LanguageList = styled.div`
  ${({ theme: { colors } }) => css`
    flex: 1;
    overflow-y: auto;
    background-color: ${colors.neutral.white};
    border-top: 1px solid ${colors.neutral.grey};
  `}
`;

const LanguageItem = styled.button<{ isSelected: boolean }>`
  ${({ theme: { colors, utils }, isSelected }) => css`
    font-size: ${utils.rem(18)};
    line-height: 1.5;
    display: flex;
    align-items: center;
    width: 100%;
    padding: ${utils.rem(8)} ${utils.rem(24)};
    border: none;
    background-color: ${isSelected ? utils.alphaHex(colors.primary.light, 10) : colors.neutral.white};
    color: ${colors.neutral.black};
    cursor: pointer;
    text-align: left;
    transition: background-color 0.2s;

    &:hover {
      background-color: ${isSelected ? utils.alphaHex(colors.primary.light, 15) : colors.neutral.grey};
    }

    &:focus {
      outline: 2px solid ${colors.primary.light};
      outline-offset: -2px;
    }

    &:not(:last-child) {
      border-bottom: 1px solid ${colors.neutral.grey};
    }
  `}
`;

const LanguageName = styled.span`
  flex: 1;
`;

const ButtonContainer = styled.div`
  ${({ theme: { colors, utils } }) => css`
    padding: ${utils.rem(12)} ${utils.rem(16)};
    background-color: ${colors.neutral.white};
    border-top: 1px solid ${colors.neutral.grey};
    margin-bottom: ${utils.rem(32)};
  `}
`;

const StyledButton = styled(Button)`
  width: 100%;
  padding: ${({ theme: { utils } }) => `${utils.rem(14)} ${utils.rem(24)}`};
`;

interface LanguageSelectionModalProps {
  isOpen: boolean;
  onLanguageSelect: (language: Language) => void;
  onClose: () => void;
}

export const LanguageSelectionModal: React.FC<LanguageSelectionModalProps> = ({
  isOpen,
  onLanguageSelect,
  onClose,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(null);
  const [lockScroll, unlockScroll] = useScrollLock();

  const languages = useMemo(() => {
    if (searchQuery.trim()) {
      return searchLanguages(searchQuery);
    }
    return getSortedLanguages();
  }, [searchQuery]);

  const handleContinue = () => {
    if (selectedLanguage) {
      onLanguageSelect(selectedLanguage);
    }
  };


  useEffect(() => {
    if (isOpen) {
      lockScroll();
    } else {
      unlockScroll();
    }
    
    return () => {
      unlockScroll();
    };
  }, [isOpen, lockScroll, unlockScroll]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <FullScreenOverlay role="dialog" aria-modal="true" aria-labelledby="language-modal-title">
      <ModalContainer>
        <Header>
          <Title id="language-modal-title">{t('profile.languageModal.title')}</Title>
          <Description>
            {t('profile.languageModal.description')}
          </Description>
        </Header>
        
        <SearchContainer>
          <SearchInput
            type="text"
            placeholder={t('profile.languageModal.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label={t('profile.languageModal.searchAriaLabel')}
            autoFocus
          />
        </SearchContainer>

        <LanguageList role="listbox" aria-label={t('profile.languageModal.listAriaLabel')}>
          {languages.map((language) => (
            <LanguageItem
              key={language.code}
              role="option"
              aria-selected={selectedLanguage?.code === language.code}
              isSelected={selectedLanguage?.code === language.code}
              onClick={() => setSelectedLanguage(language)}
              tabIndex={0}
            >
              <LanguageName>{language.name}</LanguageName>
            </LanguageItem>
          ))}
        </LanguageList>

        <ButtonContainer>
          <StyledButton
            disabled={!selectedLanguage}
            onClick={handleContinue}
            aria-label={selectedLanguage ? t('profile.languageModal.continueWith', { language: selectedLanguage.name }) : t('profile.languageModal.continueDisabled')}
            variant="contained"
          >
            {t('profile.languageModal.continue')}
          </StyledButton>
        </ButtonContainer>
      </ModalContainer>
    </FullScreenOverlay>,
    document.body
  );
};